import {useState, useEffect, useRef, useCallback, useMemo} from "react";
import {Card} from "@/components/ui/card";
import {Label} from "@/components/ui/label";
import {Input} from "@/components/ui/input";
import {Button} from "@/components/ui/button";
import {Switch} from "@/components/ui/switch";
import {Separator} from "@/components/ui/separator";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs";
import {Badge} from "@/components/ui/badge";
import {Textarea} from "@/components/ui/textarea";
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group";
import {VoicePreferencesCard} from "@/components/VoicePreferencesCard";
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from "@/components/ui/dialog";
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from "@/components/ui/accordion";
import {
    Sparkles, User, CreditCard, Bell,
    Crown, Check, UserCircle, Heart, GraduationCap, Loader2,
    Lock, Eye, EyeOff, Shield, KeyRound, Layout, Camera, Trash2, FolderUp,
    FileText, Upload, ExternalLink, DollarSign, ListOrdered, List, Clock, 
    GitBranch, Quote, Gauge, Lightbulb, AlertTriangle, MessageSquare, Target, Database, Bug, Brain
} from "lucide-react";
import { SentryErrorButton, SentryTestMessageButton } from "@/components/SentryErrorButton";
import { SourceOfTruthManager } from "@/components/settings/SourceOfTruthManager";
import { OrganizationPoliciesPanel } from "@/components/settings/OrganizationPoliciesPanel";
import {
    getThoughtProcessPreferences,
    setThoughtProcessPreferences,
    type ThoughtProcessPreferences,
    RAG_LIMIT_MIN,
    RAG_LIMIT_MAX,
    RAG_THRESHOLD_MIN,
    RAG_THRESHOLD_MAX,
} from "@/lib/thoughtProcessPreferences";
import { Slider } from "@/components/ui/slider";
import {
    applyDocumentNamingPreferences,
    getDocumentNamingPreferencesFromProfile,
    DEFAULT_DOCUMENT_NAMING_PREFERENCES,
    type DocumentNamingPreferences,
} from "@/lib/documentNamingPreferences";

function AutoExpandingTextarea({
    value,
    onChange,
    placeholder,
    minRows = 3,
    maxRows = 15,
    ...props
}: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    minRows?: number;
    maxRows?: number;
    [key: string]: any;
}) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const adjustHeight = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.style.height = 'auto';
        const lineHeight = 24;
        const minHeight = lineHeight * minRows;
        const maxHeight = lineHeight * maxRows;
        const scrollHeight = textarea.scrollHeight;
        textarea.style.height = `${Math.min(Math.max(scrollHeight, minHeight), maxHeight)}px`;
    }, [minRows, maxRows]);

    useEffect(() => {
        adjustHeight();
    }, [value, adjustHeight]);

    return (
        <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="resize-none overflow-y-auto transition-[height] duration-150"
            style={{ minHeight: `${24 * minRows}px` }}
            {...props}
        />
    );
}
import {Avatar, AvatarFallback, AvatarImage} from "@/components/ui/avatar";
import {useAuth} from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useUserProfile } from "@/contexts/UserProfileContext";
import {useQuery, useMutation} from "@tanstack/react-query";
import {useSearch, useLocation} from "wouter";
import {queryClient} from "@/lib/queryClient";
import {useToast} from "@/hooks/use-toast";
import type {UserProfile} from "@shared/schema";
import {userProfileService, type ResponsePreferences, DEFAULT_RESPONSE_PREFERENCES} from "@/services/userProfileService";
import {creditsService, type BillingSubscription} from "@/services/creditsService";
import {pinService, type PinStatus} from "@/services/pinService";
import {knowledgeBaseService} from "@/services/knowledgeBaseService";
import {chatThreadsService} from "@/services/chatThreadsService";
import CreditsDashboard from "@/pages/CreditsDashboard";
import {GoogleDriveExport} from "@/components/GoogleDriveExport";
import {EmbeddingManager} from "@/components/EmbeddingManager";
import {formatDistanceToNow} from "date-fns";

type SubscriptionPlan = {
    id: string;
    name: string;
    displayName: string;
    monthlyCredits: string;
    priceUsd: string;
    features: string[];
};

export default function Settings() {
    const searchString = useSearch();
    const [, setLocation] = useLocation();
    const urlParams = new URLSearchParams(searchString);
    const tabParam = urlParams.get("tab");
    const [activeTab, setActiveTabState] = useState(tabParam || "profile");

    const setActiveTab = useCallback((value: string) => {
        setActiveTabState(value);
        setLocation(`/settings?tab=${value}`, { replace: true });
    }, [setLocation]);
    const {user} = useAuth();
    const { currentWorkspaceId } = useWorkspace();
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

    useEffect(() => {
        if (tabParam) {
            setActiveTabState(tabParam);
        }
    }, [tabParam]);

    const { profileWithAvatar: userProfile, refetch: refetchProfile } = useUserProfile();

    const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast({
                title: "File too large",
                description: "Please select an image under 2MB",
                variant: "destructive",
            });
            return;
        }

        setIsUploadingPhoto(true);
        try {
            const reader = new FileReader();
            reader.onload = async (event) => {
                const base64 = event.target?.result as string;
                await userProfileService.updateProfile({avatarUrl: base64});
                await refetchProfile();
                queryClient.invalidateQueries({queryKey: ["user-profile"]});
                toast({
                    title: "Photo Updated",
                    description: "Your profile photo has been updated.",
                });
                setIsUploadingPhoto(false);
            };
            reader.readAsDataURL(file);
        } catch (error) {
            console.error("Error uploading photo:", error);
            toast({
                title: "Upload Failed",
                description: "Failed to upload photo. Please try again.",
                variant: "destructive",
            });
            setIsUploadingPhoto(false);
        }
    };

    const handleRemovePhoto = async () => {
        setIsUploadingPhoto(true);
        try {
            await userProfileService.updateProfile({avatarUrl: ""});
            await refetchProfile();
            queryClient.invalidateQueries({queryKey: ["user-profile"]});
            toast({
                title: "Photo Removed",
                description: "Your profile photo has been removed.",
            });
        } catch (error) {
            console.error("Error removing photo:", error);
            toast({
                title: "Remove Failed",
                description: "Failed to remove photo. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsUploadingPhoto(false);
        }
    };

    const {data: billingData} = useQuery<BillingSubscription>({
        queryKey: ["billing-subscription"],
        queryFn: () => creditsService.getBillingSubscription(),
    });

    const {data: exportDocuments = []} = useQuery({
        queryKey: ["export-documents", currentWorkspaceId ?? null],
        queryFn: () => knowledgeBaseService.getKnowledgeBaseItems(undefined, currentWorkspaceId ?? null),
        enabled: activeTab === "backup",
    });

    const {data: exportChatThreads = []} = useQuery({
        queryKey: ["export-chat-threads", currentWorkspaceId ?? null],
        queryFn: () => chatThreadsService.getChatThreadsForExport(currentWorkspaceId ?? null),
        enabled: activeTab === "backup",
    });

    const {toast} = useToast();

    // Profile & Identity state
    const [personality, setPersonality] = useState("");
    const [worldview, setWorldview] = useState("");
    const [learningGoals, setLearningGoals] = useState("");

    // Personalization state
    const [contentComplexity, setContentComplexity] = useState("intermediate");
    const [responseLength, setResponseLength] = useState("balanced");
    const [communicationStyle, setCommunicationStyle] = useState("professional");
    const [favoriteTopics, setFavoriteTopics] = useState("");
    const [avoidTopics, setAvoidTopics] = useState("");
    const [learningStyle, setLearningStyle] = useState("visual");
    const [contentStructure, setContentStructure] = useState("structured");
    const [learningPace, setLearningPace] = useState("3");

    // Voice & Style state
    const [writingStyle, setWritingStyle] = useState("");
    const [writingSamples, setWritingSamples] = useState("");
    const [favoritePhrases, setFavoritePhrases] = useState("");
    const [referenceAuthors, setReferenceAuthors] = useState("");
    const [communicationPreferences, setCommunicationPreferences] = useState("");

    // PIN Security state
    const [currentPin, setCurrentPin] = useState("");
    const [newPin, setNewPin] = useState("");
    const [confirmPin, setConfirmPin] = useState("");
    const [showCurrentPin, setShowCurrentPin] = useState(false);
    const [showNewPin, setShowNewPin] = useState(false);
    const [showConfirmPin, setShowConfirmPin] = useState(false);
    const [pinError, setPinError] = useState("");
    // Security tab: unlock via password (forgot PIN) – separate flow from PIN Active
    const [unlockedViaPassword, setUnlockedViaPassword] = useState(false);
    const [accountPassword, setAccountPassword] = useState("");
    const [showAccountPassword, setShowAccountPassword] = useState(false);
    const [resetNewPin, setResetNewPin] = useState("");
    const [resetConfirmPin, setResetConfirmPin] = useState("");
    const [showResetNewPin, setShowResetNewPin] = useState(false);
    const [showResetConfirmPin, setShowResetConfirmPin] = useState(false);

    // Resume extraction state
    const [isUploadingResume, setIsUploadingResume] = useState(false);
    const [resumeFileName, setResumeFileName] = useState("");
    const [resumeMimeType, setResumeMimeType] = useState("");
    const [resumeFileSize, setResumeFileSize] = useState<number | null>(null);
    const [resumeExtractedText, setResumeExtractedText] = useState("");
    const [showResumeModal, setShowResumeModal] = useState(false);

    // AI Response Style Preferences state
    const [responsePrefs, setResponsePrefs] = useState<ResponsePreferences>(DEFAULT_RESPONSE_PREFERENCES);
    const [documentNamingPrefs, setDocumentNamingPrefs] = useState<DocumentNamingPreferences>(
        DEFAULT_DOCUMENT_NAMING_PREFERENCES
    );

    // Interface preferences
    const [proMode, setProMode] = useState(() => {
        if (typeof window !== "undefined") {
            return localStorage.getItem("pro_mode") === "true";
        }
        return false;
    });
    const [compactMode, setCompactMode] = useState(() => {
        if (typeof window !== "undefined") {
            return localStorage.getItem("compact_mode") === "true";
        }
        return false;
    });

    const [thoughtPrefs, setThoughtPrefs] = useState<ThoughtProcessPreferences>(() =>
        getThoughtProcessPreferences()
    );

    const updateThoughtPref = useCallback(<K extends keyof ThoughtProcessPreferences>(
        key: K,
        value: ThoughtProcessPreferences[K]
    ) => {
        setThoughtPrefs((prev) => {
            const next = { ...prev, [key]: value };
            setThoughtProcessPreferences(next);
            return next;
        });
    }, []);

    const handleProModeChange = (enabled: boolean) => {
        setProMode(enabled);
        localStorage.setItem("pro_mode", enabled.toString());
        // Trigger a custom event so sidebar can react
        window.dispatchEvent(new CustomEvent("proModeChange", {detail: enabled}));
        toast({
            title: enabled ? "Pro Mode Enabled" : "Pro Mode Disabled",
            description: enabled
                ? "Advanced features are now visible in the sidebar"
                : "Sidebar shows only essential features",
        });
    };

    const handleCompactModeChange = (enabled: boolean) => {
        setCompactMode(enabled);
        localStorage.setItem("compact_mode", enabled.toString());
    };

    // PIN status query
    const {data: pinStatus, isLoading: pinStatusLoading, refetch: refetchPinStatus} = useQuery<PinStatus>({
        queryKey: ["pin-status"],
        queryFn: () => pinService.getStatus(),
        retry: false,
    });

    // Set PIN mutation
    const setPinMutation = useMutation({
        mutationFn: async (data: { currentPin?: string; newPin: string }) => {
            return await pinService.setPin(data.newPin, data.currentPin);
        },
        onSuccess: () => {
            setCurrentPin("");
            setNewPin("");
            setConfirmPin("");
            setPinError("");
            refetchPinStatus();
            toast({
                title: "PIN Updated",
                description: "Your security PIN has been set successfully.",
            });
        },
        onError: (error: Error) => {
            setPinError(error.message);
        },
    });

    // Verify PIN mutation (for unlocking settings)
    const verifyPinMutation = useMutation({
        mutationFn: async (pin: string) => {
            return await pinService.verifyPin(pin);
        },
        onSuccess: () => {
            setCurrentPin("");
            setPinError("");
            refetchPinStatus();
            toast({
                title: "PIN Verified",
                description: "You can now manage your PIN settings.",
            });
        },
        onError: (error: Error) => {
            setPinError(error.message);
        },
    });

    // Verify account password on Security tab (forgot-PIN flow – session invalid)
    const verifyPasswordMutation = useMutation({
        mutationFn: async (password: string) => {
            return await pinService.verifyAccountPassword(password);
        },
        onSuccess: () => {
            setAccountPassword("");
            setPinError("");
            setUnlockedViaPassword(true);
            toast({
                title: "Verified",
                description: "Enter your new PIN below.",
            });
        },
        onError: (error: Error) => {
            setPinError(error.message);
        },
    });

    // Set new PIN without current PIN (after password verification on Security tab)
    const setPinWithoutCurrentPinMutation = useMutation({
        mutationFn: async (newPin: string) => {
            return await pinService.setPinWithoutCurrentPin(newPin);
        },
        onSuccess: () => {
            setResetNewPin("");
            setResetConfirmPin("");
            setPinError("");
            setUnlockedViaPassword(false);
            refetchPinStatus();
            toast({
                title: "PIN Updated",
                description: "Your security PIN has been set successfully.",
            });
        },
        onError: (error: Error) => {
            setPinError(error.message);
        },
    });

    // Handle PIN form submission
    const handleSetPin = () => {
        setPinError("");

        if (!/^\d{4,8}$/.test(newPin)) {
            setPinError("PIN must be 4-8 digits");
            return;
        }

        if (newPin !== confirmPin) {
            setPinError("PINs do not match");
            return;
        }

        setPinMutation.mutate({
            currentPin: pinStatus?.hasPinSet ? currentPin : undefined,
            newPin,
        });
    };

    const handleVerifyPin = () => {
        setPinError("");
        if (!currentPin) {
            setPinError("Please enter your PIN");
            return;
        }
        verifyPinMutation.mutate(currentPin);
    };

    const handleVerifyPassword = () => {
        setPinError("");
        if (!accountPassword) {
            setPinError("Please enter your account password");
            return;
        }
        verifyPasswordMutation.mutate(accountPassword);
    };

    const handleSetPinWithoutCurrentPin = () => {
        setPinError("");
        if (!/^\d{4,8}$/.test(resetNewPin)) {
            setPinError("PIN must be 4-8 digits");
            return;
        }
        if (resetNewPin !== resetConfirmPin) {
            setPinError("PINs do not match");
            return;
        }
        setPinWithoutCurrentPinMutation.mutate(resetNewPin);
    };

    // Fetch user profile for personalization form
    const { profile: profileData, isLoading: profileLoading } = useUserProfile();

    // Sync profile data to state when loaded
    useEffect(() => {
        if (profileData) {
            // Profile & Identity
            setPersonality(profileData.background || "");
            setWorldview(profileData.worldview || "");
            setLearningGoals(profileData.goals?.join(", ") || "");
            // Personalization - extract preferences object
            const prefs = profileData.preferences as Record<string, any> | null;
            setContentComplexity(profileData.verbosity || "intermediate");
            setResponseLength(prefs?.responseLength || "balanced");
            setCommunicationStyle(profileData.tone || "professional");
            setFavoriteTopics(profileData.expertise?.join(", ") || "");
            setAvoidTopics(profileData.avoidPhrases?.join(", ") || "");
            setLearningStyle(profileData.learningStyle || "visual");
            setContentStructure(profileData.responseFormat || "structured");
            // Extract pace from preferences if stored
            if (prefs?.learningPace) {
                setLearningPace(String(prefs.learningPace));
            }
            // Voice & Style fields
            setWritingStyle(profileData.writingStyle || "");
            setWritingSamples(profileData.writingSamples?.join("\n\n") || "");
            setFavoritePhrases(profileData.favoritePhrases?.join(", ") || "");
            setReferenceAuthors(profileData.referenceAuthors?.join(", ") || "");
            setCommunicationPreferences(profileData.communicationPreferences || "");
            // Resume fields (extracted text)
            setResumeFileName((profileData as any).resume_file_name || (profileData as any).resumeFileName || "");
            setResumeMimeType((profileData as any).resume_mime_type || "");
            setResumeFileSize((profileData as any).resume_file_size || null);
            setResumeExtractedText((profileData as any).resume_text || "");
            // AI Response Style Preferences
            const respPrefs = (profileData as any).response_preferences || (profileData as any).responsePreferences;
            if (respPrefs) {
                setResponsePrefs({...DEFAULT_RESPONSE_PREFERENCES, ...respPrefs});
            }
            setDocumentNamingPrefs(getDocumentNamingPreferencesFromProfile(profileData));
        }
    }, [profileData]);

    // Save Profile mutation
    const saveProfileMutation = useMutation({
        mutationFn: async (data: Partial<UserProfile>) => {
            return await userProfileService.upsertUserProfile(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["user-profile"]});
            toast({
                title: "Profile saved",
                description: "Your profile has been updated successfully.",
            });
        },
        onError: (error: Error) => {
            toast({
                title: "Error saving profile",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    // Handle save profile & identity
    const handleSaveProfile = () => {
        saveProfileMutation.mutate({
            background: personality,
            worldview: worldview,
            goals: learningGoals.split(",").map(g => g.trim()).filter(Boolean),
        });
    };

    // Handle save personalization
    const handleSavePersonalization = () => {
        const prefs = (profileData?.preferences as Record<string, any>) || {};
        saveProfileMutation.mutate({
            verbosity: contentComplexity,
            tone: communicationStyle,
            expertise: favoriteTopics.split(",").map(t => t.trim()).filter(Boolean),
            avoidPhrases: avoidTopics.split(",").map(t => t.trim()).filter(Boolean),
            learningStyle: learningStyle,
            responseFormat: contentStructure,
            preferences: {...prefs, learningPace, responseLength},
        });
    };

    // Handle save voice & style
    const handleSaveVoiceStyle = () => {
        saveProfileMutation.mutate({
            writingStyle: writingStyle,
            writingSamples: writingSamples.split("\n\n").map(s => s.trim()).filter(Boolean),
            favoritePhrases: favoritePhrases.split(",").map(p => p.trim()).filter(Boolean),
            referenceAuthors: referenceAuthors.split(",").map(a => a.trim()).filter(Boolean),
            communicationPreferences: communicationPreferences,
        });
    };

    // Handle resume text extraction
    const handleResumeUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploadingResume(true);
        try {
            const result = await userProfileService.extractResumeText(file);
            setResumeFileName(result.fileName);
            setResumeMimeType(result.mimeType);
            setResumeFileSize(result.fileSize);
            setResumeExtractedText(result.extractedText);
            queryClient.invalidateQueries({queryKey: ["user-profile"]});
            queryClient.invalidateQueries({queryKey: ["user-profile"]});
            toast({
                title: "Resume Processed",
                description: "Text has been extracted from your resume successfully.",
            });
        } catch (error: any) {
            console.error("Error extracting resume text:", error);
            toast({
                title: "Extraction Failed",
                description: error.message || "Failed to extract text from resume. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsUploadingResume(false);
        }
    };

    // Handle resume delete
    const handleDeleteResume = async () => {
        setIsUploadingResume(true);
        try {
            await userProfileService.deleteResume();
            setResumeFileName("");
            setResumeMimeType("");
            setResumeFileSize(null);
            setResumeExtractedText("");
            queryClient.invalidateQueries({queryKey: ["user-profile"]});
            queryClient.invalidateQueries({queryKey: ["user-profile"]});
            toast({
                title: "Resume Removed",
                description: "Your resume information has been removed.",
            });
        } catch (error: any) {
            console.error("Error deleting resume:", error);
            toast({
                title: "Delete Failed",
                description: error.message || "Failed to remove resume. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsUploadingResume(false);
        }
    };

    // Handle save response preferences
    const handleSaveResponsePreferences = async () => {
        try {
            await userProfileService.updateResponsePreferences(responsePrefs);
            queryClient.invalidateQueries({queryKey: ["user-profile"]});
            queryClient.invalidateQueries({queryKey: ["user-profile"]});
            toast({
                title: "Response Preferences Saved",
                description: "Your AI response style preferences have been updated.",
            });
        } catch (error: any) {
            console.error("Error saving response preferences:", error);
            toast({
                title: "Save Failed",
                description: error.message || "Failed to save preferences. Please try again.",
                variant: "destructive",
            });
        }
    };

    // Update a single response preference
    const updateResponsePref = (key: keyof ResponsePreferences, value: any) => {
        setResponsePrefs(prev => ({...prev, [key]: value}));
    };

    const handleSaveDocumentNamingPreferences = async () => {
        try {
            await userProfileService.updateDocumentNamingPreferences(documentNamingPrefs);
            queryClient.invalidateQueries({queryKey: ["user-profile"]});
            toast({
                title: "Document Naming Saved",
                description: "Your document naming preferences have been updated.",
            });
        } catch (error: any) {
            console.error("Error saving document naming preferences:", error);
            toast({
                title: "Save Failed",
                description: error.message || "Failed to save document naming preferences. Please try again.",
                variant: "destructive",
            });
        }
    };

    const updateDocumentNamingPref = <K extends keyof DocumentNamingPreferences>(
        key: K,
        value: DocumentNamingPreferences[K]
    ) => {
        setDocumentNamingPrefs((prev) => ({ ...prev, [key]: value }));
    };

    const documentNamingPreview = useMemo(
        () =>
            applyDocumentNamingPreferences("RESEARCH: model comparison notes", {
                topicTitle: "AI Strategy",
                createdAt: "2026-05-14T00:00:00.000Z",
                preferences: documentNamingPrefs,
            }),
        [documentNamingPrefs]
    );

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold mb-2">Settings</h1>
                <p className="text-muted-foreground">Manage your account, preferences, and personalization.</p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList className="flex flex-wrap gap-1 h-auto min-h-10">
                    <TabsTrigger value="profile" data-testid="tab-profile">
                        <UserCircle className="h-4 w-4 mr-2"/>
                        Profile
                    </TabsTrigger>
                    <TabsTrigger value="personalization" data-testid="tab-personalization">
                        <Heart className="h-4 w-4 mr-2"/>
                        Personalization
                    </TabsTrigger>
                    <TabsTrigger value="account" data-testid="tab-account">
                        <User className="h-4 w-4 mr-2"/>
                        Account
                    </TabsTrigger>
                    <TabsTrigger value="subscription" data-testid="tab-subscription">
                        <CreditCard className="h-4 w-4 mr-2"/>
                        Subscription
                    </TabsTrigger>
                    <TabsTrigger value="notifications" data-testid="tab-notifications">
                        <Bell className="h-4 w-4 mr-2"/>
                        Notifications
                    </TabsTrigger>
                    <TabsTrigger value="interface" data-testid="tab-interface">
                        <Layout className="h-4 w-4 mr-2"/>
                        Interface
                    </TabsTrigger>
                    <TabsTrigger value="embeddings" data-testid="tab-embeddings">
                        <Database className="h-4 w-4 mr-2"/>
                        Embeddings
                    </TabsTrigger>
                    <TabsTrigger value="security" data-testid="tab-security">
                        <Shield className="h-4 w-4 mr-2"/>
                        Security
                    </TabsTrigger>
                    <TabsTrigger value="backup" data-testid="tab-backup">
                        <FolderUp className="h-4 w-4 mr-2"/>
                        Backup & Export
                    </TabsTrigger>
                    <TabsTrigger value="thought-process" data-testid="tab-thought-process">
                        <Brain className="h-4 w-4 mr-2"/>
                        Thought process
                    </TabsTrigger>
                    <TabsTrigger value="developer" data-testid="tab-developer">
                        <Bug className="h-4 w-4 mr-2"/>
                        Developer
                    </TabsTrigger>
                </TabsList>

                {/* Profile Tab - User Identity & Goals */}
                <TabsContent value="profile" className="space-y-6">
                    <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-2">Profile & Identity</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Capture your personality, worldview, and preferences for personalized AI experiences
                        </p>
                        {profileLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground"/>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <Accordion type="multiple" defaultValue={["personality", "worldview", "goals"]} className="space-y-2">
                                    <AccordionItem value="personality" className="border rounded-md px-4">
                                        <AccordionTrigger className="hover:no-underline">
                                            <div className="flex items-center gap-2">
                                                <UserCircle className="h-4 w-4 text-primary"/>
                                                <span className="font-medium">Personality & Interests</span>
                                                {personality && (
                                                    <Badge variant="secondary" className="ml-2 text-xs">
                                                        {personality.length > 50 ? `${personality.substring(0, 50)}...` : personality}
                                                    </Badge>
                                                )}
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div className="space-y-2 pt-2">
                                                <AutoExpandingTextarea
                                                    value={personality}
                                                    onChange={setPersonality}
                                                    placeholder="Describe your personality, interests, and what matters to you..."
                                                    minRows={4}
                                                    maxRows={20}
                                                    data-testid="textarea-personality"
                                                />
                                                <p className="text-xs text-muted-foreground">
                                                    This helps the AI understand your perspective and provide more relevant responses.
                                                </p>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>

                                    <AccordionItem value="worldview" className="border rounded-md px-4">
                                        <AccordionTrigger className="hover:no-underline">
                                            <div className="flex items-center gap-2">
                                                <Heart className="h-4 w-4 text-primary"/>
                                                <span className="font-medium">Worldview & Values</span>
                                                {worldview && (
                                                    <Badge variant="secondary" className="ml-2 text-xs">
                                                        {worldview.length > 50 ? `${worldview.substring(0, 50)}...` : worldview}
                                                    </Badge>
                                                )}
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div className="space-y-2 pt-2">
                                                <AutoExpandingTextarea
                                                    value={worldview}
                                                    onChange={setWorldview}
                                                    placeholder="Share your worldview, core values, and beliefs..."
                                                    minRows={4}
                                                    maxRows={20}
                                                    data-testid="textarea-worldview"
                                                />
                                                <p className="text-xs text-muted-foreground">
                                                    Your values shape how AI contextualizes information for you.
                                                </p>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>

                                    <AccordionItem value="goals" className="border rounded-md px-4">
                                        <AccordionTrigger className="hover:no-underline">
                                            <div className="flex items-center gap-2">
                                                <Target className="h-4 w-4 text-primary"/>
                                                <span className="font-medium">Learning Goals</span>
                                                {learningGoals && (
                                                    <Badge variant="secondary" className="ml-2 text-xs">
                                                        {learningGoals.split(',').length} goal{learningGoals.split(',').length !== 1 ? 's' : ''}
                                                    </Badge>
                                                )}
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div className="space-y-2 pt-2">
                                                <AutoExpandingTextarea
                                                    value={learningGoals}
                                                    onChange={setLearningGoals}
                                                    placeholder="What are you trying to learn or achieve? (comma-separated goals)&#10;&#10;Example:&#10;Master Python programming, Learn machine learning basics, Improve writing skills"
                                                    minRows={3}
                                                    maxRows={10}
                                                    data-testid="input-goals"
                                                />
                                                <p className="text-xs text-muted-foreground">
                                                    Separate multiple goals with commas.
                                                </p>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>

                                <Button
                                    className="w-full mt-4"
                                    onClick={handleSaveProfile}
                                    disabled={saveProfileMutation.isPending}
                                    data-testid="button-save-profile-identity"
                                >
                                    {saveProfileMutation.isPending ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                                            Saving...
                                        </>
                                    ) : (
                                        "Save Profile"
                                    )}
                                </Button>
                            </div>
                        )}
                    </Card>

                    {/* Resume Upload Section */}
                    <Card className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <FileText className="h-5 w-5 text-primary"/>
                            <h3 className="text-lg font-semibold">Resume / CV</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-6">
                            Upload your resume to extract text so AI can understand your professional background and provide career-relevant insights
                        </p>

                        <div className="space-y-4">
                            {resumeExtractedText ? (
                                <div className="flex items-center gap-4 p-4 border rounded-md bg-muted/30">
                                    <FileText className="h-10 w-10 text-muted-foreground flex-shrink-0"/>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate">{resumeFileName || "Resume"}</p>
                                        <div className="text-sm text-muted-foreground space-y-1">
                                            <p>Text extracted successfully</p>
                                            {resumeMimeType && (
                                                <p className="text-xs">Type: {resumeMimeType}</p>
                                            )}
                                            {resumeFileSize && (
                                                <p className="text-xs">Size: {(resumeFileSize / 1024).toFixed(2)} KB</p>
                                            )}
                                            <p className="text-xs">Characters: {resumeExtractedText.length.toLocaleString()}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 flex-shrink-0">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setShowResumeModal(true)}
                                            data-testid="button-view-resume"
                                        >
                                            <ExternalLink className="h-4 w-4 mr-2"/>
                                            View
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleDeleteResume}
                                            disabled={isUploadingResume}
                                            data-testid="button-delete-resume"
                                        >
                                            {isUploadingResume ? (
                                                <Loader2 className="h-4 w-4 animate-spin"/>
                                            ) : (
                                                <>
                                                    <Trash2 className="h-4 w-4 mr-2"/>
                                                    Remove
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div 
                                    className="border-2 border-dashed rounded-md p-8 text-center hover-elevate cursor-pointer"
                                    onClick={() => document.getElementById("resume-upload-input")?.click()}
                                    data-testid="dropzone-resume"
                                >
                                    {isUploadingResume ? (
                                        <div className="flex flex-col items-center gap-2">
                                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground"/>
                                            <p className="text-sm text-muted-foreground">Extracting text...</p>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center gap-2">
                                            <Upload className="h-8 w-8 text-muted-foreground"/>
                                            <p className="font-medium">Click to upload your resume</p>
                                            <p className="text-sm text-muted-foreground">PDF, DOC, DOCX, TXT, or other text-based files (max 10MB)</p>
                                        </div>
                                    )}
                                </div>
                            )}
                            <input
                                id="resume-upload-input"
                                type="file"
                                accept=".pdf,.doc,.docx,.txt,.md,.rtf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                                className="hidden"
                                onChange={handleResumeUpload}
                            />
                        </div>
                    </Card>
                </TabsContent>

                {/* Personalization Tab - Content Curation & Learning Style */}
                <TabsContent value="personalization" className="space-y-6">
                    <Card className="p-6">
                        <SourceOfTruthManager />
                    </Card>
                    <Card className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <Heart className="h-5 w-5 text-primary"/>
                            <h3 className="text-lg font-semibold">Content Curation</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-6">
                            Tailor AI responses and content to match your characteristics
                        </p>

                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="space-y-3">
                                    <Label>Content Complexity</Label>
                                    <RadioGroup value={contentComplexity} onValueChange={setContentComplexity}>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="beginner" id="beginner"/>
                                            <Label htmlFor="beginner" className="font-normal">Beginner-friendly</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="intermediate" id="intermediate"/>
                                            <Label htmlFor="intermediate" className="font-normal">Intermediate</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="advanced" id="advanced"/>
                                            <Label htmlFor="advanced" className="font-normal">Advanced/Technical</Label>
                                        </div>
                                    </RadioGroup>
                                </div>

                                <div className="space-y-3">
                                    <Label>Response Length</Label>
                                    <RadioGroup value={responseLength} onValueChange={setResponseLength}>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="concise" id="concise"/>
                                            <Label htmlFor="concise" className="font-normal">Concise</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="balanced" id="balanced"/>
                                            <Label htmlFor="balanced" className="font-normal">Balanced</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="detailed" id="detailed"/>
                                            <Label htmlFor="detailed" className="font-normal">Detailed</Label>
                                        </div>
                                    </RadioGroup>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-3">
                                    <Label>Communication Style</Label>
                                    <RadioGroup value={communicationStyle} onValueChange={setCommunicationStyle}>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="casual" id="casual"/>
                                            <Label htmlFor="casual" className="font-normal">Casual & Friendly</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="professional" id="professional"/>
                                            <Label htmlFor="professional" className="font-normal">Professional</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="academic" id="academic"/>
                                            <Label htmlFor="academic" className="font-normal">Academic/Formal</Label>
                                        </div>
                                    </RadioGroup>
                                </div>

                                <div className="space-y-2">
                                    <Label>Favorite Topics</Label>
                                    <Input
                                        placeholder="e.g., AI, Productivity, Science (comma-separated)"
                                        value={favoriteTopics}
                                        onChange={(e) => setFavoriteTopics(e.target.value)}
                                        data-testid="input-topics"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Topics to Avoid</Label>
                                    <Input
                                        placeholder="e.g., Politics, Sports (comma-separated)"
                                        value={avoidTopics}
                                        onChange={(e) => setAvoidTopics(e.target.value)}
                                        data-testid="input-avoid-topics"
                                    />
                                </div>
                            </div>
                        </div>

                        <Button
                            className="w-full mt-6"
                            onClick={handleSavePersonalization}
                            disabled={saveProfileMutation.isPending}
                            data-testid="button-save-curation"
                        >
                            {saveProfileMutation.isPending ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                                    Saving...
                                </>
                            ) : (
                                "Save Curation Preferences"
                            )}
                        </Button>
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <GraduationCap className="h-5 w-5 text-primary"/>
                            <h3 className="text-lg font-semibold">Learning Style</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-6">
                            Adjust how content is presented based on your learning behavior
                        </p>

                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="space-y-3">
                                    <Label>Learning Style Preference</Label>
                                    <RadioGroup value={learningStyle} onValueChange={setLearningStyle}>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="visual" id="visual"/>
                                            <Label htmlFor="visual" className="font-normal">Visual (diagrams,
                                                charts)</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="textual" id="textual"/>
                                            <Label htmlFor="textual" className="font-normal">Textual (detailed
                                                explanations)</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="practical" id="practical"/>
                                            <Label htmlFor="practical" className="font-normal">Practical (examples &
                                                exercises)</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="mixed" id="mixed"/>
                                            <Label htmlFor="mixed" className="font-normal">Mixed approach</Label>
                                        </div>
                                    </RadioGroup>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-3">
                                    <Label>Content Structure</Label>
                                    <RadioGroup value={contentStructure} onValueChange={setContentStructure}>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="structured" id="structured"/>
                                            <Label htmlFor="structured" className="font-normal">Structured
                                                (step-by-step)</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="exploratory" id="exploratory"/>
                                            <Label htmlFor="exploratory" className="font-normal">Exploratory (discover
                                                as you go)</Label>
                                        </div>
                                    </RadioGroup>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="pace">Learning Pace</Label>
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm text-muted-foreground">Slow</span>
                                        <input
                                            type="range"
                                            id="pace"
                                            min="1"
                                            max="5"
                                            value={learningPace}
                                            onChange={(e) => setLearningPace(e.target.value)}
                                            className="flex-1"
                                            data-testid="slider-pace"
                                        />
                                        <span className="text-sm text-muted-foreground">Fast</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <Button
                            className="w-full mt-6"
                            onClick={handleSavePersonalization}
                            disabled={saveProfileMutation.isPending}
                            data-testid="button-save-learning"
                        >
                            {saveProfileMutation.isPending ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                                    Saving...
                                </>
                            ) : (
                                "Save Learning Preferences"
                            )}
                        </Button>
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="h-5 w-5 text-primary"/>
                            <h3 className="text-lg font-semibold">Voice & Style</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-6">
                            Define your unique writing style so AI can match your voice
                        </p>

                        <div className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="writingStyle">Writing Style</Label>
                                <Textarea
                                    id="writingStyle"
                                    placeholder="Describe your preferred writing style (e.g., conversational, formal, concise, uses humor, technical...)"
                                    value={writingStyle}
                                    onChange={(e) => setWritingStyle(e.target.value)}
                                    rows={3}
                                    data-testid="input-writing-style"
                                />
                                <p className="text-xs text-muted-foreground">How would you describe your communication
                                    style?</p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="communicationPreferences">Communication Preferences</Label>
                                <Textarea
                                    id="communicationPreferences"
                                    placeholder="Describe how you prefer to communicate (e.g., direct and to the point, collaborative, visual learner...)"
                                    value={communicationPreferences}
                                    onChange={(e) => setCommunicationPreferences(e.target.value)}
                                    rows={2}
                                    data-testid="input-communication-preferences"
                                />
                                <p className="text-xs text-muted-foreground">How do you prefer information to be
                                    presented?</p>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="favoritePhrases">Favorite Phrases</Label>
                                    <Input
                                        id="favoritePhrases"
                                        placeholder="Phrases you like to use (comma-separated)"
                                        value={favoritePhrases}
                                        onChange={(e) => setFavoritePhrases(e.target.value)}
                                        data-testid="input-favorite-phrases"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="referenceAuthors">Reference Authors/Styles</Label>
                                    <Input
                                        id="referenceAuthors"
                                        placeholder="Authors or styles to emulate (comma-separated)"
                                        value={referenceAuthors}
                                        onChange={(e) => setReferenceAuthors(e.target.value)}
                                        data-testid="input-reference-authors"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="writingSamples">Writing Samples</Label>
                                <Textarea
                                    id="writingSamples"
                                    placeholder="Paste examples of your writing (separate samples with blank lines)"
                                    value={writingSamples}
                                    onChange={(e) => setWritingSamples(e.target.value)}
                                    rows={4}
                                    data-testid="input-writing-samples"
                                />
                                <p className="text-xs text-muted-foreground">Share examples so AI can learn your
                                    authentic voice</p>
                            </div>
                        </div>

                        <Button
                            className="w-full mt-6"
                            onClick={handleSaveVoiceStyle}
                            disabled={saveProfileMutation.isPending}
                            data-testid="button-save-voice-style"
                        >
                            {saveProfileMutation.isPending ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                                    Saving...
                                </>
                            ) : (
                                "Save Voice & Style"
                            )}
                        </Button>
                    </Card>

                    {/* Read-aloud (TTS) voice preferences */}
                    <VoicePreferencesCard/>

                    {/* AI Response Style Preferences Card */}
                    <Card className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <MessageSquare className="h-5 w-5 text-primary"/>
                            <h3 className="text-lg font-semibold">AI Response Style</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-6">
                            Control how AI formats and structures its responses to you
                        </p>

                        <div className="space-y-6">
                            {/* Content Preferences */}
                            <div className="space-y-4">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Content Preferences</h4>
                                
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                        <div className="flex items-center gap-3">
                                            <ExternalLink className="h-5 w-5 text-muted-foreground flex-shrink-0"/>
                                            <div>
                                                <Label className="font-medium">Include URLs</Label>
                                                <p className="text-xs text-muted-foreground">Add clickable links when mentioning resources</p>
                                            </div>
                                        </div>
                                        <Switch
                                            checked={responsePrefs.includeUrls}
                                            onCheckedChange={(v) => updateResponsePref("includeUrls", v)}
                                            data-testid="switch-include-urls"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                        <div className="flex items-center gap-3">
                                            <DollarSign className="h-5 w-5 text-muted-foreground flex-shrink-0"/>
                                            <div>
                                                <Label className="font-medium">Include Costs</Label>
                                                <p className="text-xs text-muted-foreground">Show pricing and cost estimates when relevant</p>
                                            </div>
                                        </div>
                                        <Switch
                                            checked={responsePrefs.includeCosts}
                                            onCheckedChange={(v) => updateResponsePref("includeCosts", v)}
                                            data-testid="switch-include-costs"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                        <div className="flex items-center gap-3">
                                            <ListOrdered className="h-5 w-5 text-muted-foreground flex-shrink-0"/>
                                            <div>
                                                <Label className="font-medium">Action Plans</Label>
                                                <p className="text-xs text-muted-foreground">Provide step-by-step instructions</p>
                                            </div>
                                        </div>
                                        <Switch
                                            checked={responsePrefs.includeActionPlans}
                                            onCheckedChange={(v) => updateResponsePref("includeActionPlans", v)}
                                            data-testid="switch-include-action-plans"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                        <div className="flex items-center gap-3">
                                            <Clock className="h-5 w-5 text-muted-foreground flex-shrink-0"/>
                                            <div>
                                                <Label className="font-medium">Time Estimates</Label>
                                                <p className="text-xs text-muted-foreground">Include timelines for tasks</p>
                                            </div>
                                        </div>
                                        <Switch
                                            checked={responsePrefs.includeTimelines}
                                            onCheckedChange={(v) => updateResponsePref("includeTimelines", v)}
                                            data-testid="switch-include-timelines"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                        <div className="flex items-center gap-3">
                                            <GitBranch className="h-5 w-5 text-muted-foreground flex-shrink-0"/>
                                            <div>
                                                <Label className="font-medium">Alternatives</Label>
                                                <p className="text-xs text-muted-foreground">Suggest alternative approaches</p>
                                            </div>
                                        </div>
                                        <Switch
                                            checked={responsePrefs.includeAlternatives}
                                            onCheckedChange={(v) => updateResponsePref("includeAlternatives", v)}
                                            data-testid="switch-include-alternatives"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                        <div className="flex items-center gap-3">
                                            <Lightbulb className="h-5 w-5 text-muted-foreground flex-shrink-0"/>
                                            <div>
                                                <Label className="font-medium">Examples</Label>
                                                <p className="text-xs text-muted-foreground">Include practical examples</p>
                                            </div>
                                        </div>
                                        <Switch
                                            checked={responsePrefs.includeExamples}
                                            onCheckedChange={(v) => updateResponsePref("includeExamples", v)}
                                            data-testid="switch-include-examples"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                        <div className="flex items-center gap-3">
                                            <AlertTriangle className="h-5 w-5 text-muted-foreground flex-shrink-0"/>
                                            <div>
                                                <Label className="font-medium">Warnings</Label>
                                                <p className="text-xs text-muted-foreground">Highlight risks and cautions</p>
                                            </div>
                                        </div>
                                        <Switch
                                            checked={responsePrefs.includeWarnings}
                                            onCheckedChange={(v) => updateResponsePref("includeWarnings", v)}
                                            data-testid="switch-include-warnings"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                        <div className="flex items-center gap-3">
                                            <Quote className="h-5 w-5 text-muted-foreground flex-shrink-0"/>
                                            <div>
                                                <Label className="font-medium">Cite Sources</Label>
                                                <p className="text-xs text-muted-foreground">Reference sources for claims</p>
                                            </div>
                                        </div>
                                        <Switch
                                            checked={responsePrefs.citeSources}
                                            onCheckedChange={(v) => updateResponsePref("citeSources", v)}
                                            data-testid="switch-cite-sources"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Format Preferences */}
                            <Separator/>
                            <div className="space-y-4">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Format Preferences</h4>
                                
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                        <div className="flex items-center gap-3">
                                            <List className="h-5 w-5 text-muted-foreground flex-shrink-0"/>
                                            <div>
                                                <Label className="font-medium">Use Bullet Points</Label>
                                                <p className="text-xs text-muted-foreground">Prefer bullets over paragraphs</p>
                                            </div>
                                        </div>
                                        <Switch
                                            checked={responsePrefs.useBulletPoints}
                                            onCheckedChange={(v) => updateResponsePref("useBulletPoints", v)}
                                            data-testid="switch-use-bullets"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                        <div className="flex items-center gap-3">
                                            <Gauge className="h-5 w-5 text-muted-foreground flex-shrink-0"/>
                                            <div>
                                                <Label className="font-medium">Show Confidence</Label>
                                                <p className="text-xs text-muted-foreground">Indicate certainty level</p>
                                            </div>
                                        </div>
                                        <Switch
                                            checked={responsePrefs.showConfidence}
                                            onCheckedChange={(v) => updateResponsePref("showConfidence", v)}
                                            data-testid="switch-show-confidence"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Max Paragraph Length</Label>
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm text-muted-foreground w-12">Short</span>
                                        <input
                                            type="range"
                                            min="1"
                                            max="5"
                                            value={responsePrefs.maxParagraphLength}
                                            onChange={(e) => updateResponsePref("maxParagraphLength", parseInt(e.target.value))}
                                            className="flex-1"
                                            data-testid="slider-paragraph-length"
                                        />
                                        <span className="text-sm text-muted-foreground w-12 text-right">Long</span>
                                        <Badge variant="secondary">{responsePrefs.maxParagraphLength} sentences</Badge>
                                    </div>
                                </div>
                            </div>

                            {/* Custom Instructions */}
                            <Separator/>
                            <div className="space-y-2">
                                <Label htmlFor="customInstructions">Custom Instructions</Label>
                                <Textarea
                                    id="customInstructions"
                                    placeholder="Add any other specific instructions for how AI should respond to you..."
                                    value={responsePrefs.customInstructions}
                                    onChange={(e) => updateResponsePref("customInstructions", e.target.value)}
                                    rows={3}
                                    data-testid="textarea-custom-instructions"
                                />
                                <p className="text-xs text-muted-foreground">
                                    These will be included in every AI conversation
                                </p>
                            </div>
                        </div>

                        <Button
                            className="w-full mt-6"
                            onClick={handleSaveResponsePreferences}
                            data-testid="button-save-response-prefs"
                        >
                            Save Response Preferences
                        </Button>
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <FileText className="h-5 w-5 text-primary"/>
                            <h3 className="text-lg font-semibold">Document Naming</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-6">
                            Enable naming rules for AI-generated document titles. When disabled, the app falls back to the
                            default AI title. When enabled, the selected options shape the final title consistently.
                        </p>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                <div className="space-y-1">
                                    <Label className="font-medium">Enable custom naming rules</Label>
                                    <p className="text-xs text-muted-foreground">
                                        Use the enabled options below to modify document titles instead of leaving the AI title untouched.
                                    </p>
                                </div>
                                <Switch
                                    checked={documentNamingPrefs.enabled}
                                    onCheckedChange={(v) => updateDocumentNamingPref("enabled", v)}
                                    data-testid="switch-document-naming-enabled"
                                />
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                    <div className="space-y-1">
                                        <Label className="font-medium">Include topic title</Label>
                                        <p className="text-xs text-muted-foreground">
                                            If chat has a selected topic, place it at the start of the document name.
                                        </p>
                                    </div>
                                    <Switch
                                        checked={documentNamingPrefs.includeTopicTitle}
                                        onCheckedChange={(v) => updateDocumentNamingPref("includeTopicTitle", v)}
                                        data-testid="switch-document-naming-topic"
                                    />
                                </div>

                                <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                    <div className="space-y-1">
                                        <Label className="font-medium">Include AI subject prefix</Label>
                                        <p className="text-xs text-muted-foreground">
                                            Keep the one-word or short subject prefix the AI generates before the descriptor.
                                        </p>
                                    </div>
                                    <Switch
                                        checked={documentNamingPrefs.includeSubjectPrefix}
                                        onCheckedChange={(v) => updateDocumentNamingPref("includeSubjectPrefix", v)}
                                        data-testid="switch-document-naming-subject"
                                    />
                                </div>

                                <div className="flex items-center justify-between gap-4 p-3 rounded-md border md:col-span-2">
                                    <div className="space-y-1">
                                        <Label className="font-medium">Append created date</Label>
                                        <p className="text-xs text-muted-foreground">
                                            Add the draft creation date in parentheses at the end, like <code>(2026-05-14)</code>.
                                        </p>
                                    </div>
                                    <Switch
                                        checked={documentNamingPrefs.appendCreatedDate}
                                        onCheckedChange={(v) => updateDocumentNamingPref("appendCreatedDate", v)}
                                        data-testid="switch-document-naming-date"
                                    />
                                </div>
                            </div>

                            <div className="rounded-md border bg-muted/30 p-4">
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <Label className="font-medium">Preview</Label>
                                    {!documentNamingPrefs.enabled && (
                                        <Badge variant="secondary">AI fallback</Badge>
                                    )}
                                </div>
                                <p className="text-sm">{documentNamingPreview}</p>
                                <p className="text-xs text-muted-foreground mt-2">
                                    Example input: <code>RESEARCH: model comparison notes</code> with selected topic <code>AI Strategy</code>.
                                </p>
                            </div>
                        </div>

                        <Button
                            className="w-full mt-6"
                            onClick={handleSaveDocumentNamingPreferences}
                            data-testid="button-save-document-naming-prefs"
                        >
                            Save Document Naming Preferences
                        </Button>
                    </Card>
                </TabsContent>

                {/* Account Tab */}
                <TabsContent value="account" className="space-y-6">
                    <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-4">Account Information</h3>
                        <div className="space-y-6">
                            <div className="flex items-start gap-6">
                                <div className="relative">
                                    <Avatar className="h-24 w-24">
                                        <AvatarImage src={userProfile?.avatarUrl} alt="Profile" />
                                        <AvatarFallback className="text-2xl bg-primary/10">
                                            {user?.username?.charAt(0).toUpperCase() || "U"}
                                        </AvatarFallback>
                                    </Avatar>
                                    {isUploadingPhoto && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                                            <Loader2 className="h-6 w-6 animate-spin text-white" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2">
                                    <Label className="text-sm font-medium">Profile Photo</Label>
                                    <p className="text-sm text-muted-foreground mb-2">
                                        Upload a photo to personalize your profile (max 2MB)
                                    </p>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={isUploadingPhoto}
                                            onClick={() => document.getElementById("settings-photo-upload")?.click()}
                                        >
                                            <Camera className="h-4 w-4 mr-2" />
                                            {userProfile?.avatarUrl ? "Change Photo" : "Upload Photo"}
                                        </Button>
                                        {userProfile?.avatarUrl && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                disabled={isUploadingPhoto}
                                                onClick={handleRemovePhoto}
                                            >
                                                <Trash2 className="h-4 w-4 mr-2" />
                                                Remove
                                            </Button>
                                        )}
                                    </div>
                                    <input
                                        id="settings-photo-upload"
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handlePhotoUpload}
                                    />
                                </div>
                            </div>
                            <Separator />
                            <div className="space-y-2">
                                <Label htmlFor="name">Display Name</Label>
                                <Input id="name" defaultValue={user?.username || "User"} data-testid="input-name"/>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input id="email" type="email" defaultValue={user?.username || ""}
                                       data-testid="input-email" disabled className="bg-muted"/>
                            </div>
                            <Button data-testid="button-save-account">Save Changes</Button>
                        </div>
                    </Card>

                    <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-4">Preferences</h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label>Dark Mode</Label>
                                    <p className="text-sm text-muted-foreground">Use dark theme across the app</p>
                                </div>
                                <Switch data-testid="switch-dark-mode"/>
                            </div>
                            <Separator/>
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label>Auto-save Notes</Label>
                                    <p className="text-sm text-muted-foreground">Automatically save as you type</p>
                                </div>
                                <Switch defaultChecked data-testid="switch-auto-save"/>
                            </div>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="subscription" className="space-y-6">
                    <CreditsDashboard/>
                </TabsContent>

                <TabsContent value="notifications" className="space-y-6">
                    <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-4">Notification Preferences</h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label>Daily Digest</Label>
                                    <p className="text-sm text-muted-foreground">Receive daily summary of insights</p>
                                </div>
                                <Switch defaultChecked data-testid="switch-daily-digest"/>
                            </div>
                            <Separator/>
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label>AI Insights</Label>
                                    <p className="text-sm text-muted-foreground">Get notified of new AI-generated
                                        insights</p>
                                </div>
                                <Switch defaultChecked data-testid="switch-ai-insights"/>
                            </div>
                            <Separator/>
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label>Weekly Reports</Label>
                                    <p className="text-sm text-muted-foreground">Summary of your knowledge growth</p>
                                </div>
                                <Switch data-testid="switch-weekly-reports"/>
                            </div>
                        </div>
                    </Card>
                </TabsContent>

                {/* Interface Tab - Display Preferences */}
                <TabsContent value="interface" className="space-y-6">
                    <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-2">Display Preferences</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            Customize how the app looks and behaves
                        </p>

                        <div className="space-y-6">
                            {/* Pro Mode Toggle */}
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <div className="flex items-center gap-2">
                                        <Label htmlFor="pro-mode" className="font-medium">Pro Mode</Label>
                                        <Badge variant="secondary" className="text-xs">
                                            <Crown className="h-3 w-3 mr-1"/>
                                            Advanced
                                        </Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        Show advanced features in the sidebar (Command Center, Playbooks, Automations,
                                        Missions)
                                    </p>
                                </div>
                                <Switch
                                    id="pro-mode"
                                    checked={proMode}
                                    onCheckedChange={handleProModeChange}
                                    data-testid="switch-pro-mode"
                                />
                            </div>

                            <Separator/>

                            {/* Compact Mode Toggle */}
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label htmlFor="compact-mode" className="font-medium">Compact Mode</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Use smaller spacing and tighter layouts for more content
                                    </p>
                                </div>
                                <Switch
                                    id="compact-mode"
                                    checked={compactMode}
                                    onCheckedChange={handleCompactModeChange}
                                    data-testid="switch-compact-mode"
                                />
                            </div>
                        </div>
                    </Card>

                    <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-2">Feature Discovery</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            Control how new features are introduced
                        </p>

                        <div className="space-y-4">
                            <div className="bg-muted/50 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <div
                                        className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <Sparkles className="h-5 w-5 text-primary"/>
                                    </div>
                                    <div>
                                        <h4 className="font-medium">Feature Hints</h4>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            The app will show helpful hints as you discover new features.
                                            This helps you get the most out of Deeply Smart without overwhelming you.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="text-sm text-muted-foreground">
                                <strong>Pro Mode Features Include:</strong>
                                <ul className="list-disc list-inside mt-2 space-y-1">
                                    <li>Command Center - Unified intelligence hub</li>
                                    <li>Playbook Builder - Multi-step AI workflows</li>
                                    <li>Automations - Trigger-based actions</li>
                                    <li>Agent Missions - Long-running AI tasks</li>
                                    <li>Performance Insights - Outcome tracking</li>
                                    <li>Knowledge Graph - Visual connections</li>
                                    <li>Trend Forecasts - AI predictions</li>
                                </ul>
                            </div>
                        </div>
                    </Card>
                </TabsContent>

                {/* Security Tab - PIN Protection */}
                <TabsContent value="security" className="space-y-6">
                    <OrganizationPoliciesPanel />
                    <Card className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <Lock className="h-5 w-5 text-primary"/>
                            <h3 className="text-lg font-semibold">Thread PIN Protection</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-6">
                            Set a PIN to lock confidential threads. Locked threads require your PIN to view.
                        </p>

                        {pinStatusLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground"/>
                            </div>
                        ) : !pinStatus?.hasPinSet ? (
                            /* First time - Set PIN */
                            <div className="space-y-4">
                                <div className="bg-muted/50 p-4 rounded-lg mb-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <KeyRound className="h-4 w-4 text-primary"/>
                                        <span className="font-medium">Set Your Security PIN</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        Create a 4-8 digit PIN to protect your confidential threads.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="newPin">New PIN</Label>
                                    <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                        <Input
                                            id="newPin"
                                            type={showNewPin ? "text" : "password"}
                                            placeholder="Enter 4-8 digit PIN"
                                            value={newPin}
                                            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                                            maxLength={8}
                                            className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                            data-testid="input-new-pin"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPin(!showNewPin)}
                                            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                                            data-testid="button-toggle-new-pin"
                                        >
                                            {showNewPin ? <EyeOff className="h-4 w-4 shrink-0"/> : <Eye className="h-4 w-4 shrink-0"/>}
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPin">Confirm PIN</Label>
                                    <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                        <Input
                                            id="confirmPin"
                                            type={showConfirmPin ? "text" : "password"}
                                            placeholder="Confirm your PIN"
                                            value={confirmPin}
                                            onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                                            maxLength={8}
                                            className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                            data-testid="input-confirm-pin"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPin(!showConfirmPin)}
                                            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                                            data-testid="button-toggle-confirm-pin"
                                        >
                                            {showConfirmPin ? <EyeOff className="h-4 w-4 shrink-0"/> : <Eye className="h-4 w-4 shrink-0"/>}
                                        </button>
                                    </div>
                                </div>

                                {pinError && (
                                    <p className="text-sm text-destructive">{pinError}</p>
                                )}

                                <Button
                                    className="w-full"
                                    onClick={handleSetPin}
                                    disabled={setPinMutation.isPending || !newPin || !confirmPin}
                                    data-testid="button-set-pin"
                                >
                                    {setPinMutation.isPending ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                                            Setting PIN...
                                        </>
                                    ) : (
                                        <>
                                            <Lock className="h-4 w-4 mr-2"/>
                                            Set PIN
                                        </>
                                    )}
                                </Button>
                            </div>
                        ) : !pinStatus?.isSessionValid ? (
                            /* Security tab: session invalid – choose Unlock with PIN or Verify with password (forgot-PIN flow) */
                            !unlockedViaPassword ? (
                                <div className="space-y-6">
                                    <div className="bg-muted/50 p-4 rounded-lg mb-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Lock className="h-4 w-4 text-amber-500"/>
                                            <span className="font-medium">Unlock to manage PIN</span>
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            If you remember your PIN, unlock with PIN below. If you forgot your PIN, verify with your account password to set a new one. Session times out after 30 minutes of inactivity.
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="text-sm text-muted-foreground">Current PIN:</span>
                                        <Badge variant="secondary" className="font-mono tracking-widest">
                                            ****
                                        </Badge>
                                        {pinStatus?.pinSetAt && (
                                            <span className="text-xs text-muted-foreground">
                                                (set {formatDistanceToNow(new Date(pinStatus.pinSetAt))} ago)
                                            </span>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="currentPinVerify">Unlock with PIN</Label>
                                        <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                            <Input
                                                id="currentPinVerify"
                                                type={showCurrentPin ? "text" : "password"}
                                                placeholder="Enter your PIN"
                                                value={currentPin}
                                                onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                                                maxLength={8}
                                                className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                                data-testid="input-current-pin-verify"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowCurrentPin(!showCurrentPin)}
                                                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                                                data-testid="button-toggle-current-pin"
                                            >
                                                {showCurrentPin ? <EyeOff className="h-4 w-4 shrink-0"/> : <Eye className="h-4 w-4 shrink-0"/>}
                                            </button>
                                        </div>
                                        <Button
                                            className="w-full"
                                            onClick={handleVerifyPin}
                                            disabled={verifyPinMutation.isPending || !currentPin}
                                            data-testid="button-verify-pin"
                                        >
                                            {verifyPinMutation.isPending ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                                                    Verifying...
                                                </>
                                            ) : (
                                                <>
                                                    <Lock className="h-4 w-4 mr-2"/>
                                                    Unlock with PIN
                                                </>
                                            )}
                                        </Button>
                                    </div>

                                    <Separator/>

                                    <div className="space-y-2">
                                        <Label htmlFor="accountPasswordUnlock">Or verify with account password (forgot PIN)</Label>
                                        <p className="text-xs text-muted-foreground">
                                            Re-enter your account password to set a new PIN without entering the current PIN.
                                        </p>
                                        <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                            <Input
                                                id="accountPasswordUnlock"
                                                type={showAccountPassword ? "text" : "password"}
                                                placeholder="Enter your account password"
                                                value={accountPassword}
                                                onChange={(e) => setAccountPassword(e.target.value)}
                                                className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                                data-testid="input-account-password-unlock"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowAccountPassword(!showAccountPassword)}
                                                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                                            >
                                                {showAccountPassword ? <EyeOff className="h-4 w-4 shrink-0"/> : <Eye className="h-4 w-4 shrink-0"/>}
                                            </button>
                                        </div>
                                        <Button
                                            variant="secondary"
                                            className="w-full"
                                            onClick={handleVerifyPassword}
                                            disabled={verifyPasswordMutation.isPending || !accountPassword}
                                            data-testid="button-verify-password-unlock"
                                        >
                                            {verifyPasswordMutation.isPending ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                                                    Verifying...
                                                </>
                                            ) : (
                                                "Verify and set new PIN"
                                            )}
                                        </Button>
                                    </div>

                                    {pinError && (
                                        <p className="text-sm text-destructive">{pinError}</p>
                                    )}
                                </div>
                            ) : (
                                /* Forgot-PIN flow: password verified – set new PIN (separate from PIN Active) */
                                <div className="space-y-4">
                                    <div className="bg-green-500/10 p-4 rounded-lg mb-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Check className="h-4 w-4 text-green-500"/>
                                            <span className="font-medium text-green-700 dark:text-green-400">Account verified</span>
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            You verified with your password. Set a new PIN below (no current PIN required).
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="resetNewPin">New PIN</Label>
                                        <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                            <Input
                                                id="resetNewPin"
                                                type={showResetNewPin ? "text" : "password"}
                                                placeholder="Enter new 4-8 digit PIN"
                                                value={resetNewPin}
                                                onChange={(e) => setResetNewPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                                                maxLength={8}
                                                className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowResetNewPin(!showResetNewPin)}
                                                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                                            >
                                                {showResetNewPin ? <EyeOff className="h-4 w-4 shrink-0"/> : <Eye className="h-4 w-4 shrink-0"/>}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="resetConfirmPin">Confirm new PIN</Label>
                                        <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                            <Input
                                                id="resetConfirmPin"
                                                type={showResetConfirmPin ? "text" : "password"}
                                                placeholder="Confirm new PIN"
                                                value={resetConfirmPin}
                                                onChange={(e) => setResetConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                                                maxLength={8}
                                                className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowResetConfirmPin(!showResetConfirmPin)}
                                                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                                            >
                                                {showResetConfirmPin ? <EyeOff className="h-4 w-4 shrink-0"/> : <Eye className="h-4 w-4 shrink-0"/>}
                                            </button>
                                        </div>
                                    </div>
                                    {pinError && (
                                        <p className="text-sm text-destructive">{pinError}</p>
                                    )}
                                    <Button
                                        className="w-full"
                                        onClick={handleSetPinWithoutCurrentPin}
                                        disabled={setPinWithoutCurrentPinMutation.isPending || !resetNewPin || !resetConfirmPin}
                                        data-testid="button-set-new-pin"
                                    >
                                        {setPinWithoutCurrentPinMutation.isPending ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                                                Setting PIN...
                                            </>
                                        ) : (
                                            "Set new PIN"
                                        )}
                                    </Button>
                                </div>
                            )
                        ) : (
                            /* PIN is set and session is valid - Can change PIN */
                            <div className="space-y-4">
                                <div className="bg-green-500/10 p-4 rounded-lg mb-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Check className="h-4 w-4 text-green-500"/>
                                        <span
                                            className="font-medium text-green-700 dark:text-green-400">PIN Active</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        Your PIN is set. Session unlocked for 30 minutes.
                                    </p>
                                </div>

                                <div className="flex items-center gap-2 mb-4">
                                    <span className="text-sm text-muted-foreground">Current PIN:</span>
                                    <Badge variant="secondary" className="font-mono tracking-widest">
                                        ****
                                    </Badge>
                                    {pinStatus?.pinSetAt && (
                                        <span className="text-xs text-muted-foreground">
                      (set {formatDistanceToNow(new Date(pinStatus.pinSetAt))} ago)
                    </span>
                                    )}
                                </div>

                                <Separator/>

                                <h4 className="font-medium mt-4">Change PIN</h4>

                                <div className="space-y-2">
                                    <Label htmlFor="currentPinChange">Current PIN</Label>
                                    <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                        <Input
                                            id="currentPinChange"
                                            type={showCurrentPin ? "text" : "password"}
                                            placeholder="Enter current PIN"
                                            value={currentPin}
                                            onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                                            maxLength={8}
                                            className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                            data-testid="input-current-pin-change"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowCurrentPin(!showCurrentPin)}
                                            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                                        >
                                            {showCurrentPin ? <EyeOff className="h-4 w-4 shrink-0"/> : <Eye className="h-4 w-4 shrink-0"/>}
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="newPinChange">New PIN</Label>
                                    <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                        <Input
                                            id="newPinChange"
                                            type={showNewPin ? "text" : "password"}
                                            placeholder="Enter new 4-8 digit PIN"
                                            value={newPin}
                                            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                                            maxLength={8}
                                            className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                            data-testid="input-new-pin-change"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPin(!showNewPin)}
                                            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                                        >
                                            {showNewPin ? <EyeOff className="h-4 w-4 shrink-0"/> : <Eye className="h-4 w-4 shrink-0"/>}
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPinChange">Confirm New PIN</Label>
                                    <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                        <Input
                                            id="confirmPinChange"
                                            type={showConfirmPin ? "text" : "password"}
                                            placeholder="Confirm new PIN"
                                            value={confirmPin}
                                            onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                                            maxLength={8}
                                            className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                            data-testid="input-confirm-pin-change"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPin(!showConfirmPin)}
                                            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                                        >
                                            {showConfirmPin ? <EyeOff className="h-4 w-4 shrink-0"/> : <Eye className="h-4 w-4 shrink-0"/>}
                                        </button>
                                    </div>
                                </div>

                                {pinError && (
                                    <p className="text-sm text-destructive">{pinError}</p>
                                )}

                                <Button
                                    className="w-full"
                                    onClick={handleSetPin}
                                    disabled={setPinMutation.isPending || !currentPin || !newPin || !confirmPin}
                                    data-testid="button-change-pin"
                                >
                                    {setPinMutation.isPending ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                                            Changing PIN...
                                        </>
                                    ) : (
                                        <>
                                            <KeyRound className="h-4 w-4 mr-2"/>
                                            Change PIN
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <Shield className="h-5 w-5 text-primary"/>
                            <h3 className="text-lg font-semibold">About PIN Protection</h3>
                        </div>
                        <div className="space-y-3 text-sm text-muted-foreground">
                            <p>
                                PIN protection allows you to lock individual threads to keep sensitive conversations
                                private.
                            </p>
                            <ul className="list-disc list-inside space-y-1">
                                <li>Lock any thread by clicking the lock icon</li>
                                <li>Locked threads require your PIN to view</li>
                                <li>PIN session expires after 30 minutes of inactivity</li>
                                <li>Your PIN is securely encrypted and never stored in plain text</li>
                            </ul>
                        </div>
                    </Card>
                </TabsContent>

                {/* Semantic Search / Embeddings Tab */}
                <TabsContent value="embeddings" className="space-y-6">
                    <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-2">Semantic Search & Embeddings</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            Manage document embeddings for semantic search. Embeddings enable the AI to find relevant information based on meaning, not just keywords.
                        </p>
                    </Card>
                    <EmbeddingManager />
                </TabsContent>

                {/* Backup & Export Tab */}
                <TabsContent value="backup" className="space-y-6">
                    <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-2">Backup & Export</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            Export your documents and chat threads to external services for backup and portability.
                        </p>
                    </Card>

                    <GoogleDriveExport
                        documents={exportDocuments.map(doc => ({
                            id: doc.id,
                            title: doc.title,
                            content: doc.content || '',
                        }))}
                        chatThreads={exportChatThreads.map(thread => ({
                            id: thread.id,
                            title: thread.title || 'Untitled Chat',
                            messages: (Array.isArray(thread.messages) ? thread.messages : []).map((msg: any) => ({
                                role: msg.role,
                                content: msg.content,
                                timestamp: msg.timestamp,
                            })),
                        }))}
                        onExportComplete={(results) => {
                            if (results.successful.length > 0) {
                                toast({
                                    title: "Export Complete",
                                    description: `Successfully exported ${results.successful.length} file(s) to Google Drive.`,
                                });
                            }
                        }}
                    />

                    <Card className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <FolderUp className="h-5 w-5 text-primary"/>
                            <h3 className="text-lg font-semibold">About Backup & Export</h3>
                        </div>
                        <div className="space-y-3 text-sm text-muted-foreground">
                            <p>
                                Keep your knowledge safe by exporting to external cloud storage services.
                            </p>
                            <ul className="list-disc list-inside space-y-1">
                                <li>Export documents in Markdown, HTML, or plain text format</li>
                                <li>Back up chat threads with full conversation history</li>
                                <li>Files are organized in folders by type</li>
                                <li>Your data remains private and under your control</li>
                            </ul>
                        </div>
                    </Card>
                </TabsContent>

                {/* Thought process tab – chain-of-thought and RAG admin defaults */}
                <TabsContent value="thought-process" className="space-y-6">
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight">Thought process</h2>
                        <p className="text-muted-foreground text-sm mt-1">
                            Manage how Deeply Smart reasons and retrieves context. These are defaults; you can still change them in each chat.
                        </p>
                    </div>

                    <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                            <Brain className="h-5 w-5 text-primary"/>
                            Chain of thought (reasoning)
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Manage how Deeply Smart reasons. These are defaults; you can still change Thinking Mode in each chat.
                        </p>
                        <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                            <div className="space-y-0.5">
                                <Label htmlFor="default-thinking-mode" className="font-medium">Default thinking mode for new chats</Label>
                                <p className="text-xs text-muted-foreground">
                                    When on, new chats start with extended step-by-step reasoning (when the model supports it).
                                </p>
                            </div>
                            <Switch
                                id="default-thinking-mode"
                                checked={thoughtPrefs.defaultThinkingMode}
                                onCheckedChange={(v) => updateThoughtPref("defaultThinkingMode", v)}
                                data-testid="switch-default-thinking-mode"
                            />
                        </div>
                    </Card>

                    <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                            <Database className="h-5 w-5 text-primary"/>
                            RAG (retrieval)
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Control how Deeply Smart retrieves context from your knowledge base. These apply to all chats unless you change RAG in the chat bar.
                        </p>
                        <div className="space-y-6">
                            <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                <div className="space-y-0.5">
                                    <Label htmlFor="auto-embed-documents" className="font-medium">Auto-embed new documents</Label>
                                    <p className="text-xs text-muted-foreground">
                                        When on, documents you create or edit are automatically indexed into RAG memory. Turn off to save documents without embedding them.
                                    </p>
                                </div>
                                <Switch
                                    id="auto-embed-documents"
                                    checked={thoughtPrefs.autoEmbedDocuments}
                                    onCheckedChange={(v) => updateThoughtPref("autoEmbedDocuments", v)}
                                    data-testid="switch-auto-embed-documents"
                                />
                            </div>

                            <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                <div className="space-y-0.5">
                                    <Label htmlFor="auto-embed-threads" className="font-medium">Auto-embed chat threads</Label>
                                    <p className="text-xs text-muted-foreground">
                                        When on, chat threads are automatically indexed into RAG memory after messages are saved.
                                    </p>
                                </div>
                                <Switch
                                    id="auto-embed-threads"
                                    checked={thoughtPrefs.autoEmbedThreads}
                                    onCheckedChange={(v) => updateThoughtPref("autoEmbedThreads", v)}
                                    data-testid="switch-auto-embed-threads"
                                />
                            </div>

                            <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                <div className="space-y-0.5">
                                    <Label htmlFor="default-rag-on" className="font-medium">Default RAG on for new chats</Label>
                                    <p className="text-xs text-muted-foreground">
                                        When on, new chats start with semantic search over your documents.
                                    </p>
                                </div>
                                <Switch
                                    id="default-rag-on"
                                    checked={thoughtPrefs.defaultRagOn}
                                    onCheckedChange={(v) => updateThoughtPref("defaultRagOn", v)}
                                    data-testid="switch-default-rag-on"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="rag-limit">Max semantic chunks</Label>
                                <div className="flex items-center gap-3">
                                    <Input
                                        id="rag-limit"
                                        type="number"
                                        min={RAG_LIMIT_MIN}
                                        max={RAG_LIMIT_MAX}
                                        value={thoughtPrefs.ragLimit}
                                        onChange={(e) => {
                                            const n = parseInt(e.target.value, 10);
                                            if (!Number.isNaN(n)) updateThoughtPref("ragLimit", n);
                                        }}
                                        className="w-24"
                                        data-testid="input-rag-limit"
                                    />
                                    <span className="text-xs text-muted-foreground">
                                        {RAG_LIMIT_MIN}–{RAG_LIMIT_MAX} chunks per query
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Similarity threshold</Label>
                                <div className="flex items-center gap-4">
                                    <Slider
                                        value={[thoughtPrefs.ragSimilarityThreshold]}
                                        onValueChange={([v]) => updateThoughtPref("ragSimilarityThreshold", v)}
                                        min={RAG_THRESHOLD_MIN}
                                        max={RAG_THRESHOLD_MAX}
                                        step={0.05}
                                        className="flex-1 max-w-xs"
                                        data-testid="slider-rag-threshold"
                                    />
                                    <Badge variant="secondary" className="shrink-0">
                                        {(thoughtPrefs.ragSimilarityThreshold * 100).toFixed(0)}%
                                    </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Minimum similarity for a chunk to be included (higher = stricter).
                                </p>
                            </div>

                            <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                <div className="space-y-0.5">
                                    <Label htmlFor="rag-hybrid-mode" className="font-medium">Hybrid mode (explicit + semantic)</Label>
                                    <p className="text-xs text-muted-foreground">
                                        Combine space/topic/docs you selected with semantic search results. Turn off for semantic-only retrieval.
                                    </p>
                                </div>
                                <Switch
                                    id="rag-hybrid-mode"
                                    checked={thoughtPrefs.ragHybridMode}
                                    onCheckedChange={(v) => updateThoughtPref("ragHybridMode", v)}
                                    data-testid="switch-rag-hybrid-mode"
                                />
                            </div>

                            <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                <div className="space-y-0.5">
                                    <Label htmlFor="rag-hybrid-search" className="font-medium">Hybrid search (keyword + vector)</Label>
                                    <p className="text-xs text-muted-foreground">
                                        Combine full-text keyword search with vector similarity. Better for exact phrases and rare terms.
                                    </p>
                                </div>
                                <Switch
                                    id="rag-hybrid-search"
                                    checked={thoughtPrefs.ragHybridSearch}
                                    onCheckedChange={(v) => updateThoughtPref("ragHybridSearch", v)}
                                    data-testid="switch-rag-hybrid-search"
                                />
                            </div>

                            <div className="flex items-center justify-between gap-4 p-3 rounded-md border">
                                <div className="space-y-0.5">
                                    <Label htmlFor="rag-use-rerank" className="font-medium">Rerank results (Cohere)</Label>
                                    <p className="text-xs text-muted-foreground">
                                        Rerank top retrieval candidates with Cohere for better relevance. Requires COHERE_API_KEY.
                                    </p>
                                </div>
                                <Switch
                                    id="rag-use-rerank"
                                    checked={thoughtPrefs.ragUseRerank}
                                    onCheckedChange={(v) => updateThoughtPref("ragUseRerank", v)}
                                    data-testid="switch-rag-use-rerank"
                                />
                            </div>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="developer" className="space-y-6">
                    <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-2">Sentry error tracking</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Use these buttons to verify errors and messages are sent to Sentry. Check your Sentry project dashboard after clicking.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <SentryErrorButton />
                            <SentryTestMessageButton />
                        </div>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Resume Extracted Text Modal */}
            <Dialog open={showResumeModal} onOpenChange={setShowResumeModal}>
                <DialogContent className="max-w-4xl max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle>Resume Extracted Content</DialogTitle>
                        <DialogDescription>
                            {resumeFileName && (
                                <div className="mt-2 space-y-1">
                                    <p className="font-medium">{resumeFileName}</p>
                                    {resumeMimeType && <p className="text-xs text-muted-foreground">Type: {resumeMimeType}</p>}
                                    {resumeFileSize && <p className="text-xs text-muted-foreground">Size: {(resumeFileSize / 1024).toFixed(2)} KB</p>}
                                    <p className="text-xs text-muted-foreground">Characters: {resumeExtractedText.length.toLocaleString()}</p>
                                </div>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-4">
                        <Textarea
                            value={resumeExtractedText}
                            readOnly
                            className="min-h-[400px] font-mono text-sm"
                            data-testid="textarea-resume-extracted-text"
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
